import { defineStore } from 'pinia';

interface NotificationSuccess {
    type: 'success';
    description: string;
}

interface Exception {
    name: string;
    message?: string;
    errors?: { message: string }[];
}

interface NotificationError {
    type: 'error';
    description: string;
    exception: Exception;
}

export type NotificationInfo = NotificationSuccess | NotificationError;

function notificationErrorToString(notificationError: NotificationError): string {
    const e = notificationError.exception;
    if (e.message !== undefined) {
        return `${notificationError.description}(${e.name}): ${e.message}`;
    } else if (e.errors === undefined) {
        return `${e} : ${notificationError.description}`;
    } else {
        let errorString = '';
        for (let i = 0; i < e.errors.length; ++i) {
            errorString += e.errors[i].message + ' ';
        }
        return `${notificationError.description}: ${errorString}`;
    }
}

export const useNotificationStore = defineStore('counter', {
    state: () => ({
        notificationInfo: null as null | NotificationInfo,
        notificationNumber: 0,
    }),
    getters: {
        isError: (state) => {
            if (state.notificationInfo === null) {
                return false;
            }
            return state.notificationInfo.type === 'error';
        },
        isSuccess: (state) => {
            if (state.notificationInfo === null) {
                return false;
            }
            return state.notificationInfo.type === 'success';
        },
        asString(): string {
            if (this.notificationInfo === null) {
                return '';
            } else if (this.isError) {
                return notificationErrorToString(this.notificationInfo as NotificationError);
            } else {
                if (!this.isSuccess) {
                    alert('Invalid state for notificationInfo');
                }
                return this.notificationInfo.description;
            }
        },
    },
    actions: {
        _setNotificationInfo(notificationInfo: NotificationInfo) {
            this.notificationNumber++;
            this.notificationInfo = notificationInfo;
        },
        setNotificationError(description: string, exception: Exception) {
            this._setNotificationInfo({ type: 'error', description, exception });
        },
        setNotificationSuccess(description: string) {
            this._setNotificationInfo({ type: 'success', description });
        },
    },
});
